import React, { useState, useRef, useEffect } from 'react';
import { Modal, Row, Col } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { useSelector, useDispatch } from 'react-redux';
import { isEmpty } from 'lodash';
import { useTranslation } from 'react-i18next';
import noImage from '../../assets/images/no-image.png';
import * as action from '../../Pages/Library/ColorLibary/store/ColorLibrary.action';
import ImgDropzone from '../ImgDropzone/ImgDropzone';
import './CommonColor.css';
import Loader from '../Utility/Loader/Loader';
import ColourSupportingDocs from '../SupportingDocuments/colourSupportingDocs';
import UploadFileAndDocuments from '../SupportingDocuments/UploadFileAndDocuments';
import { createOptions } from '../helper';
import CustomTooltip from '../Tooltip/CustomTooltip';
import { getRemovedGroupIds, toGroupIds } from './colour-group.helpers';
import { NewSelectMultiSelect } from '../PIM/common/NewSelectMultiSelect';

const removeImg = (setImageSrc, setOriginalImage, setRemoveImage) => {
  setImageSrc('');
  setOriginalImage('');
  setRemoveImage(true);
};

const valueorEmpty = (imageSrc) => {
  return imageSrc || '';
};

const imageClass = (imageSrc) => {
  return imageSrc === noImage ? 'btn py-0 d-inline-flex btn-upload-img disabled' : 'btn py-0 d-inline-flex btn-upload-img';
};

const srcImg = (imageSrc) => {
  return imageSrc || noImage;
};

const handleImageUpload = async (type, childRef, setOriginalImage, setImageSrc, setImgUpload, setRemoveImage) => {
  if (type !== 'close') {
    const croppedFileUrl = await childRef.current.showCroppedImg();
    const { imageRes, originalImg } = croppedFileUrl;
    const ogImage = await originalImg;
    setOriginalImage(ogImage);
    if (imageRes && !isEmpty(imageRes)) setImageSrc(imageRes);
  }
  setImgUpload(false);
  setRemoveImage(false);
};

const showData = (showImageUpload, imgUploader, cardDetails) => {
  return showImageUpload ? imgUploader() : cardDetails();
};

const body = (showLoader, showImageUpload, imgUploader, cardDetails, actionBtns) => {
  return showLoader ? (
    <Loader />
  ) : (
    <>
      {showData(showImageUpload, imgUploader, cardDetails)}
      {!showImageUpload && actionBtns()}
    </>
  );
};

const colourName = (register, errors, t) => {
  return (
    <div className="common-input-control">
      <p className="field-name">
        {t('COMPONENT.COMMON_COLOUR_WAY.COMMON_COLOUR_WAY_EDIT.COLOUR_ARTWORK_NAME')}
        <span className="asterisk">*</span>
      </p>
      <input
        type="text"
        name="name"
        {...register('name', {
          required: true,
          validate: (value) => !!value.trim() || 'no whitespaces.',
        })}
        placeholder={t('COMPONENT.COMMON_COLOUR_WAY.COMMON_COLOUR_WAY_EDIT.COLOUR_ARTWORK_NAME')}
        className="form-control"
      />
      {errors.name && <span className="error-message">{t('COMPONENT.COMMON_COLOUR_WAY.COMMON_COLOUR_WAY_EDIT.COLOUR_ARTWORK_REQUIRED')}</span>}
    </div>
  );
};

const forColourData = ({ ele, id, setImageSrc, setPrevCroppedImage, setOriginalImage, setValue, setSelectedGroups, setOrgGroups }) => {
  if (ele._id === id) {
    if (ele.image) {
      const { image } = ele;
      setImageSrc(image);
      setPrevCroppedImage(image);
      const fullimage = ele.fullImage && `${ele.fullImage}`;
      setOriginalImage(fullimage);
    }
    setValue('name', ele.name);
    setValue('references', ele.newReferences);

    if (ele.description) {
      setValue('description', ele?.description);
    }
    const groupIds = toGroupIds(ele?.groups);
    setSelectedGroups(groupIds);
    setOrgGroups(groupIds);
  }
};

const forSupportingDocs = (colourSupportingDocs, setDocIds) => {
  if (!isEmpty(colourSupportingDocs)) {
    const ids = colourSupportingDocs.map((ele) => ele?._id);
    setDocIds([...ids]);
  }
};

const forImageSrc = (imageSrc, prevcroppedImage, obj, originalImage, removeImage, docIds) => {
  if (imageSrc !== prevcroppedImage) {
    obj.image = imageSrc;
    obj.fullImage = originalImage;
    obj.removeImage = removeImage;
  }
  if (!isEmpty(docIds)) {
    obj.colourDocs = docIds;
  }
};

function CommonColorWayEdit({ id, onOpen, toogle, imageUpload, setSelectedId, setState }) {
  const { t } = useTranslation('common');
  const { UserDetails, ColorLibraryDetails } = useSelector((colourState) => colourState);
  const { userInfo } = UserDetails;
  const { ColorLibList, colourSupportingDocs, ColorLibGroup } = ColorLibraryDetails;
  const dispatch = useDispatch();

  const [showLoader, setShowLoader] = useState(false);
  const [imageSrc, setImageSrc] = useState('');
  const [originalImage, setOriginalImage] = useState('');
  const [prevcroppedImage, setPrevCroppedImage] = useState('');
  const [removeImage, setRemoveImage] = useState(false);
  const [showImageUpload, setImgUpload] = useState(false);
  const [showUploader, setShowUploader] = useState(false);
  const [docIds, setDocIds] = useState(false);

  const [selectedGroups, setSelectedGroups] = useState([]);
  const [orgGroups, setOrgGroups] = useState([]);
  const [groupOption, setGroupOptions] = useState([]);
  useEffect(() => {
    const groupOptions = createOptions(ColorLibGroup, 'name', '_id');
    setGroupOptions(groupOptions);
  }, [ColorLibGroup]);

  const childRef = useRef();

  const {
    register,
    formState: { errors },
    handleSubmit,
    setValue,
  } = useForm();

  useEffect(() => {
    setShowLoader(true);
    !isEmpty(ColorLibList) &&
      ColorLibList?.forEach((ele) => {
        forColourData({
          ele,
          id,
          setImageSrc,
          setPrevCroppedImage,
          setOriginalImage,
          setValue,
          setSelectedGroups,
          setOrgGroups,
        });
      });
    setShowLoader(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ColorLibList, id]);

  const onSubmitMethod = async (data) => {
    if (showLoader) {
      return;
    }
    const obj = {
      name: data.name,
      description: data.description,
      newReferences: data.references,
      loggedInUserId: valueorEmpty(userInfo?.userId),
      groups: JSON.stringify(selectedGroups),
      removedGroups: JSON.stringify(getRemovedGroupIds(orgGroups, selectedGroups)),
    };

    forImageSrc(imageSrc, prevcroppedImage, obj, originalImage, removeImage, docIds);
    setShowLoader(true);
    await dispatch(action.editColorLibrary(obj, id, t));
    setSelectedId(null);
    setState((clstate) => ({
      ...clstate,
      colourLibList: [],
    }));
    await dispatch(action.clearReducer());
    setShowLoader(false);
    toogle();
  };

  useEffect(() => {
    forSupportingDocs(colourSupportingDocs, setDocIds);
  }, [colourSupportingDocs]);

  const uploadCalled = () => {
    setShowUploader(true);
  };

  const descriptionfiled = () => {
    return (
      <div className="common-input-control">
        <p className="field-name">{t('COMPONENT.COMMON_COLOUR_WAY.COMMON_COLOUR_WAY_EDIT.COLOUR_ARTWORK_DESCRIPTION')}</p>
        <input
          type="text"
          name="description"
          {...register('description')}
          placeholder={t('COMPONENT.COMMON_COLOUR_WAY.COMMON_COLOUR_WAY_EDIT.COLOUR_ARTWORK_DESCRIPTION')}
          className="form-control"
        />
      </div>
    );
  };

  const handleGroupChange = (selected) => {
    setSelectedGroups(toGroupIds(selected));
  };

  const imageContainer = () => {
    return (
      <div className="image-container">
        <div className="colorway-image">
          <img className="img-fluid" alt="croppedImage" src={srcImg(imageSrc)} />
        </div>
        <div className="d-flex justify-content-between flex-column">
          <CustomTooltip placement="right" tooltipText={t('COMPONENT.COMMON_COLOUR_WAY.COMMON_COLOUR_WAY_EDIT.UPLOAD_IMAGE')}>
            <button
              className="btn py-0 d-inline-flex btn-upload-img"
              type="button"
              onClick={() => {
                imageUpload();
                setImgUpload(true);
              }}
            >
              <span className="icon-Upload-1 f-20" aria-hidden="true" />
            </button>
          </CustomTooltip>
          {imageSrc && (
            <CustomTooltip placement="right" tooltipText={t('COMPONENT.COMMON_COLOUR_WAY.COMMON_COLOUR_WAY_EDIT.REMOVE_IMAGE')}>
              <button className={imageClass(imageSrc)} type="button" onClick={() => removeImg(setImageSrc, setOriginalImage, setRemoveImage)}>
                <span className="icon-delete" aria-hidden="true" />
              </button>
            </CustomTooltip>
          )}
        </div>
      </div>
    );
  };

  const cardDetails = () => {
    return (
      <>
        <Row>
          <Col lg="12">
            <div className="d-flex flex-wrap">
              {imageContainer()}
              <div className="flex-fill px-2">
                <Row className="form-row">
                  <Col lg="4">{colourName(register, errors, t)}</Col>
                  <Col lg="4">
                    <div className="common-input-control">
                      <p className="field-name">
                        {t('COMPONENT.COMMON_COLOUR_WAY.COMMON_COLOUR_WAY_EDIT.COLOUR_ARTWORK_REF')}
                        <span className="asterisk">*</span>
                      </p>
                      <input
                        type="text"
                        name="references"
                        {...register('references', {
                          required: true,
                          validate: (value) => !!value.trim() || 'no whitespaces.',
                        })}
                        placeholder={t('COMPONENT.COMMON_COLOUR_WAY.COMMON_COLOUR_WAY_EDIT.COLOUR_ARTWORK_REF')}
                        className="form-control"
                      />
                      {errors.references && (
                        <span className="error-message">{t('COMPONENT.COMMON_COLOUR_WAY.COMMON_COLOUR_WAY_EDIT.COLOUR_ARTWORK_REF_REQUIRED')}</span>
                      )}
                    </div>
                  </Col>
                  <Col lg="4">{descriptionfiled()}</Col>

                  <Col lg="4">
                    <div className="common-react-select">
                      <p className="field-name">{t('COMPONENT.COMMON_COLOUR_WAY.COMMON_COLOUR_WAY_EDIT.COLOUR_ARTWORK_GROUPS')}</p>
                      <NewSelectMultiSelect
                        isMulti
                        isClearable
                        name="group"
                        options={groupOption}
                        getOptionLabel={(option) => `${option.name}`}
                        getOptionValue={(option) => `${option._id}`}
                        value={groupOption?.filter((val) => selectedGroups.includes(val._id))}
                        onChange={handleGroupChange}
                        placeholder={t('COMPONENT.COMMON_COLOUR_WAY.COMMON_COLOUR_WAY_EDIT.COLOUR_GROUPS_PLACEHOLDER') || 'Colour Groups'}
                      />
                    </div>
                  </Col>
                </Row>
              </div>
            </div>
          </Col>
        </Row>
        {!showLoader && <ColourSupportingDocs libraryId={id} openUpload={uploadCalled} topLoading={setShowLoader} />}
      </>
    );
  };

  const actionBtns = () => {
    return (
      <div className="common-model-footer">
        <button onClick={handleSubmit(onSubmitMethod)} className="btn p-0" aria-label="Update" name="update" type="button">
          <span className="icon-Tick-1 f-24" />
        </button>
      </div>
    );
  };

  const imgUploader = () => {
    return (
      <ImgDropzone
        ref={childRef}
        onSubmit={(type) => handleImageUpload(type, childRef, setOriginalImage, setImageSrc, setImgUpload, setRemoveImage)}
        image={originalImage || valueorEmpty(imageSrc)}
        acceptedFileExtenstion=".jpeg,.png,.jpg"
        isEditMode={false}
        title={t('COMPONENT.COMMON_COLOUR_WAY.COMMON_COLOUR_WAY_EDIT.COLOUR')}
      />
    );
  };

  const toggleUpload = async () => {
    setShowUploader(!showUploader);
  };

  return showUploader ? (
    <UploadFileAndDocuments showTitleField onOpen={showUploader} toggle={toggleUpload} id={id} />
  ) : (
    <Modal show={onOpen} className="common-custom-modal modal-medium-3x color-way-modal" aria-labelledby="contained-modal-title-vcenter">
      <Modal.Header>
        <Modal.Title className="f-24" id="contained-modal-title-vcenter">
          {t('COMPONENT.COMMON_COLOUR_WAY.COMMON_COLOUR_WAY_EDIT.EDIT_COLOUR_ARTWORK')}
        </Modal.Title>
        <span onClick={toogle} className="icon-close cursor-pointer text-dark text-decoration-none" />
      </Modal.Header>
      <Modal.Body>{body(showLoader, showImageUpload, imgUploader, cardDetails, actionBtns)}</Modal.Body>
    </Modal>
  );
}

export default CommonColorWayEdit;
